import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../services/authentication.service';
import { UserApiService } from '../services/user-api.service';
import { UserService } from '../services/user.service';
import { ErrorDialogService } from '../services/error-dialog.service';

export const userLoadingGuard: CanActivateFn = () => {
  const msalService = inject(MsalService);
  const authService = inject(AuthenticationService);
  const userApiService = inject(UserApiService);
  const userService = inject(UserService);
  const translateService = inject(TranslateService);
  const errorDialogService = inject(ErrorDialogService);

  // Skip re-loading if user is already in memory
  if (userService.user() !== null) {
    return of(true);
  }

  // Router guards run in parallel: without waiting for MSAL to finish processing the login
  // redirect, no account exists yet, the email is null and this guard cancels the first
  // navigation after sign-in (blank page until a manual refresh).
  // Then ensure the Graph profile (and thus the Graph-sourced email) is loaded before
  // resolving the active user's email — getActiveUserEmail() prefers graphProfile.mail
  // but that's only populated once loadGraphProfile()'s request has completed.
  return msalService.handleRedirectObservable().pipe(
    catchError(() => of(null)),
    switchMap(() => from(authService.loadGraphProfile())),
    switchMap(() => {
      const email = authService.getActiveUserEmail();
      if (!email) {
        return of(false);
      }

      return userApiService.getLocalUser(email).pipe(
        map(user => {
          if (!user.languageCode?.trim()) {
            user.languageCode = 'en'; // Default fallback
          }
          userService.setUser(user);
          translateService.use(user.languageCode.toLowerCase());
          return true;
        }),
        catchError((error) => {
          console.error(error);
          errorDialogService.showError({
            title: 'User Not Found',
            message: 'User not found. Please contact your system administrator.',
          });
          return of(false);
        }),
      );
    }),
  );
};

